import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Tooltip, Grid } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import PurchaseService from 'services/PurchaseService';
import { Link, useHistory } from 'react-router-dom';
import ListDialog from 'components/popups/ListDialog';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import Info from 'components/utils/Alert/Info';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ROUTES, SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import { ShopContext } from 'Context/ShopContext';
import { UserContext } from 'Context/UserContext';
import { InventoryContext } from 'Context/InventoryContext';
import { WarehouseContext } from 'Context/WarehouseContext';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import Grayout from 'components/utils/GrayOut/Grayout';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function PurchaseReturnHistory() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { getProducts, getStockBatches } = useContext(InventoryContext);
  const { getWarehouseProducts } = useContext(WarehouseContext);

  const { startDate, endDate } = dynamicDateRange('PURCHASE_RETURN');
  const [filterFromDate, setFilterFromDate] = useState(startDate);
  const [filterToDate, setFilterToDate] = useState(endDate);

  const [searchText, setSearchText] = useState('');
  const [PurchaseReturns, setPurchaseReturns] = useState('');
  const [viewItems, setViewItems] = useState([]);
  const [currency, setCurrency] = useState('');
  const [listTitle, setListTitle] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [deleteData, setDeleteData] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(false);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });

  const subscriptionType = shop?.subscription?.type;
  const history = useHistory();
  const userHasPurchaseWritePermission = user?.shop_permissions?.purchase_permission === USER_PERMISSIONS.WRITE;

  const CSV_COLUMNS = {
    id: tCommon('ID'),
    return_date: tOperations('Return Date'),
    vendor: {
      title: tCommon('Vendor Name'),
      getValue: (purchaseItem) => purchaseItem?.vendor?.name ?? '-',
    },
    plu: {
      title: tCommon('PLU'),
      getValue: (purchaseItem) => purchaseItem?.item?.plu,
    },
    name: {
      title: tCommon('Item Name'),
      getValue: (purchaseItem) => purchaseItem?.item?.name,
    },
    rate: {
      title: tCommon('Cost'),
      getValue: (purchaseItem) => purchaseItem?.item?.rate,
    },
    quantity: {
      title: tCommon('Quantity'),
      getValue: (purchaseItem) => purchaseItem?.item?.quantity,
    },
    tax: {
      title: tCommon('Tax'),
      getValue: (purchaseItem) => {
        return ShopsHelper.getAmountFormatted(shop, purchaseItem?.item?.tax || 0);
      },
    },
    total: {
      title: tCommon('Total'),
      getValue: (purchaseItem) => {
        const amount = purchaseItem?.item?.rate * purchaseItem?.item?.quantity + (purchaseItem?.item?.tax || 0);
        return ShopsHelper.getAmountFormatted(shop, amount);
      },
    },
    total_amount: {
      title: tCommon('Purchase Total'),
      getValue: (purchaseItem) => {
        return ShopsHelper.getAmountFormatted(shop, purchaseItem?.total_amount || 0);
      },
    },
  };

  const ONE_PURCHASE_RETURN_CSV_COLUMNS = {
    purchase_return: tCommon('ID'),
    id: tOperations('Return item ID'),
    name: tCommon('Item Name'),
    quantity: tCommon('Quantity'),
    rate: tCommon('Rate'),
    plu: tCommon('PLU'),
    tax: tCommon('Tax'),
    total: tCommon('Total'),
  };

  const handleEditPurchase = (row) => {
    history.push(ROUTES.EDIT_PURCHASE_RETURN.replace(':id', row.id));
  };
  const subTotal = (items) => {
    return items.reduce(function (a, b) {
      return a + b['total'];
    }, 0);
  };

  const handleItemsView = (row = '') => {
    if (row) {
      const selectedItems = row.items;
      setListTitle(
        `${tOperations('Purchase returned items from')} ${row?.vendor?.name || row.vendor_name_old || ''} ${tCommon(
          'On'
        )} ${row.return_date}`
      );
      let data = selectedItems.map((item) => ({
        ...item,
        name: item?.item?.name,
        total: Number(item.quantity) * Number(item.rate) + Number(item.tax),
      }));
      data = [
        ...data,
        {
          plu: '',
          name: '',
          quantity: '',
          rate: '',
          tax: tCommon('Total'),
          total: subTotal(data),
        },
      ];
      return setViewItems(data);
    }
    return setViewItems([]);
  };
  const handleDeleteClick = (data) => setDeleteData(data);
  const handlePurchaseReturnDeletion = async (row) => {
    const id = row.id;
    setDeleteData(false);
    try {
      await PurchaseService.deletePurchaseReturn(id);
      setDeleteData(null);
      getPurchaseReturn().then();

      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchEnabled = (row.items || []).some((pi) => pi.item?.is_batch_enabled);
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);

      const promises = [getProducts({ useCache: false })];
      if (isPremium && hasBatchEnabled) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }
      await Promise.all(promises);
      postAlertMessage({
        text: tOperations('Purchase return deleted successfully'),
        type: 'success',
      });
    } catch (err) {
      setDeleteData(null);
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setDeleteData(false);
    }
  };

  const toCsvBtnPressed = async () => {
    const csvFormattedPurchaseReturns = PurchaseReturns.flatMap((obj) =>
      obj.items.map((item, index) => ({
        id: obj.id,
        invoice_no: obj.invoice_no,
        bill_date: obj.bill_date,
        date: obj.date,
        vendor: obj.vendor,
        vendor_name_old: obj.vendor_name_old,
        total_amount: index === 0 ? obj.total_amount : undefined,
        item: {
          ...item,
          name: item.item.name,
        },
      }))
    );

    const csv = CsvHelper.getString(csvFormattedPurchaseReturns, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PURCHASES-RETURN-${dateTime}.csv`);

    postAlertMessage({
      text: tCommon('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getPurchaseReturnsFiltered = (PurchaseReturns, searchKey) => {
    if (!searchKey) {
      return PurchaseReturns;
    }

    const searchKeySmallCase = searchKey.toLowerCase();
    return (PurchaseReturns || []).filter(
      (purchase) =>
        (purchase.vendor?.name ?? '').toLowerCase().includes(searchKeySmallCase) ||
        (purchase?.vendor_name_old ?? '').toLowerCase().includes(searchKeySmallCase) ||
        purchase.date.includes(searchKeySmallCase) ||
        purchase.total_amount.toString().includes(searchKeySmallCase)
    );
  };

  const _getPurchaseReturnsAmountSum = (PurchaseReturns) => {
    if (!PurchaseReturns || !PurchaseReturns.length) {
      return 0;
    }

    let sum = 0;
    PurchaseReturns.forEach((purchase) => (sum += purchase.total_amount));
    return ShopsHelper.getAmountFormatted(shop, sum);
  };

  const handleCloseDetailsDialog = () => {
    setViewItems(null);
  };

  const headerData = [
    {
      label: tCommon('Vendor Name'),
      id: 'vendor',
      type: 'callback',
      viewRender: (item) => item?.vendor?.name ?? item?.vendor_name_old ?? '-',
    },
    {
      label: tOperations('Return Date'),
      id: 'return_date',
      type: 'text',
    },
    {
      label: tCommon('Total Amount'),
      id: 'total_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title={tOperations('View Details')} onClick={() => handleItemsView(item)}>
              <RemoveRedEyeIcon className={styles.iconButton} />
            </Tooltip>
            {userHasPurchaseWritePermission && (
              <>
                <Tooltip title={tOperations('Edit Purchase Return')} onClick={() => handleEditPurchase(item)}>
                  <EditIcon className={styles.iconButton} />
                </Tooltip>
                <Tooltip title={tOperations('Delete Purchase Return')} onClick={() => handleDeleteClick(item)}>
                  <DeleteIcon className={styles.iconButton} />
                </Tooltip>
              </>
            )}
          </Grid>
        );
      },
    },
  ];

  const listHeaderData = [
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Rate'),
      id: 'rate',
      type: 'text',
    },
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'text',
    },
    {
      label: tCommon('Tax'),
      id: 'tax',
      type: 'text',
    },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total)}`;
      },
    },
  ];

  const _getLoadPurchaseRequestParams = useCallback(() => {
    return {
      date_start: DateTimeHelpers.getDateYMD(filterFromDate),
      date_end: DateTimeHelpers.getDateYMD(filterToDate),
    };
  }, [filterFromDate, filterToDate]);

  const getPurchaseReturn = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const params = _getLoadPurchaseRequestParams();
      const res = await PurchaseService.getPurchaseReturnDetails(params);
      setPurchaseReturns(res);
      setCurrency(res.currency);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  }, [_getLoadPurchaseRequestParams, postAlertMessage]);

  const generatePurchaseReturnHistory = () => {
    getPurchaseReturn();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate,
      filterToDate,
    });
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      DateTimeHelpers.getDateYMD(filterFromDate) !== DateTimeHelpers.getDateYMD(prevFilterData.filterFromDate) ||
      DateTimeHelpers.getDateYMD(filterToDate) !== DateTimeHelpers.getDateYMD(prevFilterData.filterToDate)
    );
  }, [filterFromDate, filterToDate, prevFilterData.filterFromDate, prevFilterData.filterToDate]);

  useEffect(() => {
    generatePurchaseReturnHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters]);

  const PurchaseReturnsToDisplay = _getPurchaseReturnsFiltered(PurchaseReturns, searchText);
  const purchaseAmountSum = _getPurchaseReturnsAmountSum(PurchaseReturns);

  return (
    <div className={styles.contentWrapper}>
      <ConfirmPopup
        data={deleteData}
        handleClose={handleDeleteClick}
        handleOkay={handlePurchaseReturnDeletion}
        alertTitle={tOperations('Confirm Delete')}
        successBtnName={tOperations('Delete')}
        alertContent={tOperations(
          "Deleted purchase return history can't be restored, do you want to delete the Purchase History?"
        )}
      />
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tOperations('Purchase Return')} <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
          <div style={{ justifyContent: 'flex-end' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tOperations('Export As')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a', marginRight: 5 }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              {tOperations('CSV')}
            </Button>
          </div>
        )}
      </div>
      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
        <RestrictionInfo
          title={tOperations('Feature not available')}
          content={tOperations('To get purchase history options upgrade subscription to premium')}
        />
      )}
      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tOperations('Purchase Return History for')} {DateTimeHelpers.convertDateToDMY(filterFromDate)}{' '}
                  {tCommon('To')} {DateTimeHelpers.convertDateToDMY(filterToDate)}
                </h2>
              </div>
              <div className={styles.filerInputSec}>
                {userHasPurchaseWritePermission && (
                  <Link to={ROUTES.NEW_PURCHASE_RETURN} style={{ textDecorationColor: 'transparent' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ backgroundColor: '#ff851b' }}
                      className={styles.actionBtn}
                    >
                      <AddShoppingCartIcon className={styles.actionBtnIcon} />
                      {tOperations('New Purchase Return')}
                    </Button>
                  </Link>
                )}
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tOperations('Search')}
                  />
                  <Tooltip title={tCommon('Search')} placement="bottom">
                    <SearchIcon className={styles.searchIcon} />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange
                  storageKey="PURCHASE_RETURN"
                  setFilterFromDate={setFilterFromDate}
                  setFilterToDate={setFilterToDate}
                />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tOperations('Load Purchase Returns')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generatePurchaseReturnHistory}
                    disabled={isShowGenerateButton === false}
                  >
                    <SyncIcon className={styles.actionBtnIcon} />
                    {tOperations('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {isShowGenerateButton === null ? (
            <Info
              title={tOperations('Set filters and click load button')}
              content={tOperations(
                'Purchase return history are generated based on the filters. Please set filters and click load purchase button to get purchase history.'
              )}
            />
          ) : (
            (PurchaseReturns || loadingIndicator) &&
            ((PurchaseReturnsToDisplay && PurchaseReturnsToDisplay.length) || loadingIndicator ? (
              <div className={styles.tableContainer}>
                <Grayout open={isShowGenerateButton} />
                <DataTable
                  columns={headerData}
                  rows={PurchaseReturnsToDisplay || []}
                  isLoading={loadingIndicator}
                  summary={{ date: 'Total', total_amount: purchaseAmountSum }}
                />
              </div>
            ) : (
              !loadingIndicator && (
                <Info
                  title={tOperations('You have no purchase returns to list')}
                  content={tOperations("You haven't any purchase returns to list with given properties")}
                />
              )
            ))
          )}

          {viewItems && viewItems.length > 0 && (
            <ListDialog
              onClose={handleCloseDetailsDialog}
              toggleItemsTab={handleItemsView}
              tableHeader={listHeaderData}
              rowData={viewItems}
              title={listTitle}
              emptyDataTitle={tOperations('No purchase return items')}
              emptyDataContent=""
              csvColumns={ONE_PURCHASE_RETURN_CSV_COLUMNS}
            />
          )}
        </>
      )}
    </div>
  );
}

export default withConsoleBase(PurchaseReturnHistory);
